import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface DocumentOption {
  id: string;
  title: string;
  reference_code: string | null;
  category_name: string;
}

interface DocumentPickerProps {
  documents: DocumentOption[];
  value: string | null;
  onChange: (documentId: string) => void;
  disabled?: boolean;
}

function DocumentPicker({ documents, value, onChange, disabled }: DocumentPickerProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (dropdownRef.current) {
      dropdownRef.current.style.setProperty('--dropdown-top', `${position.top}px`);
      dropdownRef.current.style.setProperty('--dropdown-left', `${position.left}px`);
      dropdownRef.current.style.setProperty('--dropdown-width', `${Math.max(position.width, 280)}px`);
    }
  }, [position]);

  const selected = documents.find((d) => d.id === value);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

    const openDropdown = () => {
    if (disabled) return;
    if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const dropdownHeight = 260;
        const margin = 8;

        let top = rect.bottom + 4;
        const overflowsBottom = top + dropdownHeight > window.innerHeight - margin;

        if (overflowsBottom) {
        // pull it up just enough to fit fully on screen, still appearing "attached" near the trigger
        top = Math.max(margin, window.innerHeight - dropdownHeight - margin);
        }

        setPosition({
        top,
        left: rect.left,
        width: rect.width,
        });
    }
    setOpen((o) => !o);
    };

  const filtered = documents.filter((d) => {
    const haystack = `${d.reference_code || ''} ${d.title} ${d.category_name}`.toLowerCase();
    return haystack.includes(query.toLowerCase());
  }).slice(0, 50);

  return (
    <div className="doc-picker">
      <button
        ref={triggerRef}
        type="button"
        className="doc-picker-trigger"
        onClick={openDropdown}
        disabled={disabled}
      >
        {selected ? selected.title : '-- select document to update --'}
      </button>

      {open && createPortal(
        <div
          ref={dropdownRef}
          className="doc-picker-dropdown"
        >
          <input
            type="text"
            className="doc-picker-search"
            placeholder="Search by title or code..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <div className="doc-picker-list">
            {filtered.length === 0 && <div className="doc-picker-empty">No matches</div>}
            {filtered.map((d) => (
              <div
                key={d.id}
                className="doc-picker-option"
                onClick={() => {
                  onChange(d.id);
                  setOpen(false);
                  setQuery('');
                }}
              >
                {d.title}
                <span className="doc-picker-category">{d.category_name}</span>
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default DocumentPicker;